import { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { authAPI } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const BRAND_LOGO = "/logo-black.png";

export default function RegisterPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: location.state?.email || "",
    password: "",
  });

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) {
      toast.error("Passwort muss mindestens 8 Zeichen lang sein");
      return;
    }
    setLoading(true);
    try {
      const res = await authAPI.register({
        name: `${form.firstName} ${form.lastName}`.trim(),
        email: form.email,
        password: form.password,
      });
      login(res.data.token, res.data.user);
      toast.success("Konto erfolgreich erstellt!");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registrierung fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center px-4" style={{ fontFamily: "'Manrope', sans-serif" }}>
      <div data-testid="register-card" className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
        {/* Logo */}
        <div className="flex justify-center mb-4">
          <img
            src={BRAND_LOGO}
            alt="Performance Center"
            data-testid="register-logo"
            className="h-20 w-20 rounded-2xl object-cover"
          />
        </div>

        <h1 data-testid="register-heading" className="text-xl font-bold text-center text-[#0A0A0A] mb-1">
          Konto erstellen
        </h1>
        <p className="text-sm text-center text-[#4B5563] mb-6">
          Erstelle jetzt dein kostenloses Performance Center Konto
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            data-testid="register-firstname-input"
            type="text"
            value={form.firstName}
            onChange={handleChange("firstName")}
            placeholder="Vorname"
            className="w-full border border-gray-200 bg-white focus:ring-2 focus:ring-[#E60000] focus:border-[#E60000] rounded-lg px-4 py-3 outline-none transition-all text-base"
            required
          />
          <input
            data-testid="register-lastname-input"
            type="text"
            value={form.lastName}
            onChange={handleChange("lastName")}
            placeholder="Nachname"
            className="w-full border border-gray-200 bg-white focus:ring-2 focus:ring-[#E60000] focus:border-[#E60000] rounded-lg px-4 py-3 outline-none transition-all text-base"
            required
          />
          <input
            data-testid="register-email-input"
            type="email"
            value={form.email}
            onChange={handleChange("email")}
            placeholder="E-Mail-Adresse"
            className="w-full border border-gray-200 bg-white focus:ring-2 focus:ring-[#E60000] focus:border-[#E60000] rounded-lg px-4 py-3 outline-none transition-all text-base"
            required
          />
          <div className="relative">
            <input
              data-testid="register-password-input"
              type={showPassword ? "text" : "password"}
              value={form.password}
              onChange={handleChange("password")}
              placeholder="Passwort"
              className="w-full border border-gray-200 bg-white focus:ring-2 focus:ring-[#E60000] focus:border-[#E60000] rounded-lg px-4 py-3 pr-12 outline-none transition-all text-base"
              required
            />
            <button
              type="button"
              data-testid="register-toggle-password"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#4B5563]"
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>

          <button
            data-testid="register-submit-btn"
            type="submit"
            disabled={loading}
            className="w-full bg-[#06B6D4] hover:bg-[#0891B2] text-white font-bold tracking-wide uppercase text-sm rounded-lg py-3 transition-colors disabled:opacity-50"
          >
            {loading ? "Wird erstellt..." : "Konto erstellen"}
          </button>
        </form>

        <p className="mt-4 text-center text-xs text-[#9CA3AF]">
          Mit der Registrierung akzeptierst du unsere{" "}
          <Link to="/agb" className="text-[#06B6D4] hover:underline">AGB</Link>{" "}und{" "}
          <Link to="/datenschutz" className="text-[#06B6D4] hover:underline">Datenschutzrichtlinie</Link>.
        </p>

        <p className="mt-4 text-center text-sm text-[#4B5563]">
          Bereits ein Konto?{" "}
          <Link to="/login" data-testid="register-login-link" className="text-[#06B6D4] hover:underline font-semibold">
            Hier anmelden
          </Link>
        </p>
      </div>
    </div>
  );
}
